import type Geographic from '@/lib/geographic'

/**
 * Defines mappings between the wapi weather icon codes and the icon associated to it
 */
export const WAPI_ICONS = Object.freeze({
  'ww-03': 'thunderstorm',
  'ww-04': 'thunderstorm-rain',
  'ww-05': 'rain-snow',
  'ww-06': 'freezing-rain-snow',
  'ww-07': 'freezing-rain-snow',
  'ww-08': 'freezing-rain-snow',
  'ww-09': 'light-showers',
  'ww-10': 'freezing-rain-snow',
  'ww-11': 'light-showers',
  'ww-12': 'rain',
  'ww-13': 'rain',
  'ww-14': 'snow',
  'ww-15': 'blizzard',
  'ww-16': 'snow',
  'ww-17': 'freezing-rain-snow',
  'ww-18': 'freezing-rain-snow',
  'ww-19': 'wind',
  'ww-20': 'fog',
  'ww-21': 'fog',
  'ww-22': 'wind',
  'ww-23': 'wind',
  'ww-24': 'wind',
  'ww-25': 'cold',
  'ww-26': 'broken-cloud',
  'ww-27': 'broken-cloud',
  'ww-28': 'broken-cloud',
  'ww-29': 'few-cloud',
  'ww-30': 'few-cloud',
  'ww-31': 'clear-sky-night',
  'ww-32': 'clear-sky',
  'ww-33': 'clear-sky-night',
  'ww-34': 'clear-sky',
  'ww-35': 'freezing-rain-snow',
  'ww-36': 'hot',
  'ww-37': 'thunderstorm',
  'ww-38': 'thunderstorm-rain',
  'ww-39': 'light-showers',
  'ww-40': 'rain',
  'ww-41': 'snow',
  'ww-42': 'snow',
  'ww-43': 'blizzard',
  'ww-44': 'few-cloud',
  'ww-45': 'light-showers',
  'ww-46': 'snow',
  'ww-47': 'thunderstorm',
})

/**
 * Converts a degrees number (0-360) to a directional text (ex. North)
 * Note: offset by 22.5 so direction will be for correct degree range (N is 337.5 to 22.5 vs 0 to 45)
 * @param degrees
 */
export const convertDegreesToDirectionText = (degrees: number): string =>
  [
    'North',
    'Northeast',
    'East',
    'Southeast',
    'South',
    'Southwest',
    'West',
    'Northwest',
    'North',
  ][Math.floor((degrees + 22.5) / 45)]

/**
 * Converts a degrees number (0-360) to a directional abbreviation for 8 possible directions (ex. N)
 * Note: offset by 22.5 so direction will be for correct degree range (N is 337.5 to 22.5 vs 0 to 45)
 * @param degrees
 */
export const convertDegreesToDirection8 = (degrees: number): string =>
  ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW', 'N'][
    Math.floor((degrees + 22.5) / 45)
  ]

/**
 * Converts a degrees number (0-360) to a directional abbreviation for 16 possible directions (ex. NNE)
 * Note: offset by 11.25 so direction will be for correct degree range (N is 348.75 to 11.25 vs 0 to 22.5)
 * @param degrees
 */
export const convertDegreesToDirection16 = (degrees: number): string =>
  [
    'N',
    'NNE',
    'NE',
    'ENE',
    'E',
    'ESE',
    'SE',
    'SSE',
    'S',
    'SSW',
    'SW',
    'WSW',
    'W',
    'WNW',
    'NW',
    'NNW',
    'N',
  ][Math.floor((degrees + 11.25) / 22.5)]

/**
 * Converts a direction text (ex. North, Northwest, West, etc.) to the abbreviated form for 8 possible directions (ex. N, NW, W, etc.)
 * @param direction
 */
export const convertToAbbreviatedDirectionText = (
  direction: string
): string | undefined => {
  return (
    {
      Northwest: 'NW',
      North: 'N',
      Northeast: 'NE',
      East: 'E',
      Southeast: 'SE',
      South: 'S',
      Southwest: 'SW',
      West: 'W',
      Calm: 'Calm',
    } as Record<string, string>
  )[direction]
}

/**
 * Converts a temperature in Fahrenheit to Celsius
 * @param degrees
 */
export const convertFahrenheitToCelsius = (degrees: number): number =>
  (degrees - 32) * (5 / 9)

/**
 * Converts a temperature in Celsius to Fahrenheit
 * @param degrees
 */
export const convertCelsiusToFahrenheit = (degrees: number): number =>
  degrees * (9 / 5) + 32

/**
 * Get appropriate unit string for wind speed
 * @param isImperial
 */
export const getSpeedUnit = (isImperial: boolean): string => {
  return isImperial ? 'mph' : 'kph'
}

/**
 * Converts a speed in mph to kph
 * @param speed
 */
export const convertMphToKph = (speed: number): number => speed * 1.60934

/**
 * Converts a speed in m/s to mph
 * @param speed
 */
export const convertMeterPerSecondToMph = (speed: number): number =>
  speed * 2.23694

/**
 * Converts a speed in m/s to kph
 * @param speed
 */
export const convertMeterPerSecondToKph = (speed: number): number => speed * 3.6

/**
 * Converts a speed in kph to mph
 * @param speed
 */
export const convertKphToMph = (speed: number): number => speed / 1.60934

/**
 * Converts a pressure in inHg to mbar
 * @param pressure
 */
export const convertInHgToMbar = (pressure: number): number => pressure * 33.864

/**
 * Converts a pressure in mbar to inHg
 * @param pressure
 */
export const convertMbarToInHg = (pressure: number): number => pressure / 33.864

/**
 * Converts a length in inches to centimeters
 * @param inches
 */
export const convertInToCm = (inches: number): number => inches * 2.54

/**
 * Converts a length in centimeters to inches
 * @param centimeters
 */
export const convertCmToIn = (centimeters: number): number => centimeters / 2.54

/**
 * Converts a length in millimeters to inches
 * @param millimeters
 */
export const convertMmToIn = (millimeters: number): number => millimeters / 25.4

/**
 * Converts a length in millimeters to centimeters
 * @param millimeters
 */
export const convertMmToCm = (millimeters: number): number => millimeters / 10

/**
 * Converts a length in meters to centimeters
 * @param meters
 */
export const convertMToCm = (meters: number): number => meters * 100

/**
 * Converts a date to use the correct timezone
 * @param datetime - ISO string of a datetime
 * @param timezone - timezone representation (optional; omitting uses local timezone)
 * @returns ISO string in the correct timezone
 */
export const convertTimezone = (
  datetime: string,
  timezone?: string
): string => {
  return new Date(
    new Date(datetime).toLocaleString('en-US', { timeZone: timezone })
  ).toISOString()
}

/**
 * Convert the abbreviated pressure label into long form
 * @param label
 */
export const getLongFormPressureLabel = (label: string): string => {
  return label === 'inHg' ? 'inches of mercury' : 'millibars'
}

/**
 * Convert the abbreviated precipitation label into long form
 * @param label
 */
export const getLongFormPrecipitationLabel = (label: string): string => {
  return label === 'in' ? 'inches' : 'centimeters'
}

/**
 * Format multiple coordinates into query string for bulk api request
 * @param coords - array of multiple Geographic coordinates
 */
export const formatCoordsForBulkRequest = (coords: Geographic[]): string => {
  let coordString = ''

  coords.forEach((coord) => {
    coordString += `coords=${coord.toURLCoordParam()}&`
  })

  return coordString
}

/**
 * Convert the datetime to the correct timezone
 * @param dateTime - ISO string of date and time
 * @param offsetHours - number of hours to offset for timezone
 */
export const convertToLocalTime = (
  dateTime: string,
  offsetHours: number
): Date => {
  return new Date(
    new Date(dateTime).setHours(new Date(dateTime).getHours() + offsetHours)
  )
}
